import { defineComponent as _defineComponent } from 'vue'
import DevicesSelect from '../inputs/DevicesSelect.vue';
import AlertsMetricsSelect from '../inputs/AlertsMetricsSelect.vue';
import AlertsOperatorsSelect from '../inputs/AlertsOperatorsSelect.vue';
import AlertsActionsSelect from '../inputs/AlertsActionsSelect.vue';
import EmailsTagInput from '../inputs/EmailsTagInput.vue';
import { computed, onBeforeMount, onMounted, reactive, ref } from 'vue';
import { useI18n } from '@/composables/i18n';
import { Customer } from '@/models/tenant';
import { isNil } from '@/utils/misc';
import { DeviceModelType } from '@/models/deviceBaseModel';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import { AlertAction } from '@/models/enums/AlertsEnums';
import { EmailEntry } from '@/models/EmailEntry';
import useVuelidate from '@vuelidate/core';
import {
  numeric,
  required,
  requiredIf,
  sameAs,
  withLocalizedValidators,
} from '@/utils/validation';
import { helpers, not } from '@vuelidate/validators';
import { WccAlert } from '@/models/wccAlert';
import { workCenterAlertsService } from '@/services/workCenterAlerts.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'AlertFormModal',
  props: {
    customer: null,
    alert: null
  },
  emits: ["close", "save"],
  setup(__props: any, { emit }: { emit: ((e: 'close' | 'save') => void), expose: any, slots: any, attrs: any }) {

const props = __props as {
  customer: Customer;
  alert?: WccAlert;
};





const emptyFormData: Partial<WccAlert> = {
  id: null,
  tenantId: 0,
  deviceId: null,
  metric: undefined,
  operator: undefined,
  threshold: 0,
  action: AlertAction.None,
  recipients: [],
};
const editAlert = reactive(
  isNil(props.alert) ? emptyFormData : structuredClone(props.alert),
);

const isSubmitting = ref(false);
const defaultEmails = ref<string[]>([]);
const defaultEmailsAreLoading = ref(false);

const { t } = useI18n();

const validations = {
  deviceId: withLocalizedValidators('inputs.devices_select.label', {
    required,
  }),
  metric: {
    required,
  },
  operator: {
    required,
  },
  threshold: withLocalizedValidators('alert.threshold_value.name', {
    required,
    numeric,
  }),
  action: withLocalizedValidators('inputs.alerts_actions_select.label', {
    required: helpers.withMessage(
      () => t('form.select_validation').toString(),
      not(sameAs(AlertAction.None)),
    ),
  }),
  recipients: withLocalizedValidators('inputs.alerts_email_select.label', {
    required: requiredIf(() => editAlert.action === AlertAction.WebAndEmail),
  }),
};
const v$ = useVuelidate(validations, editAlert);

onBeforeMount(() => {
  if (isEdit.value && isNil(props.alert?.deviceId)) {
    editAlert.deviceId = 0;
  }
});

onMounted(() => {
  loadDefaultEmails();
});

async function onSubmit() {
  isSubmitting.value = true;
  try {
    const alertToSubmit = structuredClone(editAlert);
    alertToSubmit.tenantId = props.customer.id;
    if (alertToSubmit.deviceId === 0) {
      alertToSubmit.deviceId = null;
    }
    if (isEdit.value) {
      await workCenterAlertsService.update(alertToSubmit);
    } else {
      await workCenterAlertsService.create(alertToSubmit);
    }
    emit('save');
    emit('close');
  } finally {
    isSubmitting.value = false;
  }
}

function onActionInput(action: AlertAction) {
  editAlert.action = action;
  if (editAlert.action !== AlertAction.WebAndEmail) {
    editAlert.recipients = [];
  }
}

async function loadDefaultEmails() {
  if (!isEdit.value) {
    return;
  }
  defaultEmailsAreLoading.value = true;
  await workCenterAlertsService
    .getAllEmailRecipientsByAlertId(props.alert!.id!)
    .then((data) => (defaultEmails.value = data))
    .finally(() => (defaultEmailsAreLoading.value = false));
}

function onEmailSelectionChanged(entries: EmailEntry[]) {
  editAlert.recipients = entries.map((item) => item.userId);
}

const modalTitle = computed(() => {
  if (isEdit.value) {
    return t(`alert.title_edit`);
  }
  return t('alert.title_create');
});

const actionTitle = computed(() => {
  if (isEdit.value) {
    return t('action.save');
  }
  return t('action.create');
});

const isEdit = computed(() => !isNil(props.alert));

const selectedKpi = computed(() =>
  workCenterAlertsService.store.getKpiByMetric(editAlert.metric),
);

const actionOrDefault = computed(() =>
  isNil(editAlert.action) ? undefined : editAlert.action,
);

const thresholdValidationMessage = computed(
  () => v$.value.threshold.$errors[0]?.$message,
);

return { __sfc: true,props, emit, emptyFormData, editAlert, isSubmitting, defaultEmails, defaultEmailsAreLoading, t, validations, v$, onSubmit, onActionInput, loadDefaultEmails, onEmailSelectionChanged, modalTitle, actionTitle, isEdit, selectedKpi, actionOrDefault, thresholdValidationMessage, DevicesSelect, AlertsMetricsSelect, AlertsOperatorsSelect, AlertsActionsSelect, EmailsTagInput, DeviceModelType, ConsoleEnum, AlertAction }
}

})